// types.ts
export interface User {
  shared_hats: any;
  user_status: string;
  id: string;
  role: string;
  assigned_knowledge_base: string[];
  updated_at: string;
  created_at: string;
  last_login: string;
  short_term_memory_enabled: boolean;
  long_term_memory_enabled: boolean;
  permissions?: {
    files?: {
      canDownload?: boolean;
      canPreview?: boolean;
    };
  };
}

export interface UnverifiedUser {
  id: string;
  invited_at: string | null;
  days_pending: number | null;
}

export interface NewUser {
  user_id: string;
  role: string;
  user_status: string;
  permissions?: {
    files?: {
      canDownload?: boolean;
      canPreview?: boolean;
    };
  };
}

export interface DailyTokenUsage {
  date: string; // This should be in the format "YYYY-MM-DD HH:MM:SS UTC"
  inputTokens: number;
  outputTokens: number;
}

export interface UserDailyTokenData {
  email: string;
  usage: DailyTokenUsage[];
}

export interface MonthlyTokenUsage {
  month: string; // This will be in format "YYYY-MM"
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface UserMonthlyTokenData {
  email: string;
  usage: MonthlyTokenUsage[];
}

export interface PaginationData {
  page: number;
  items_per_page: number;
  total_pages: number;
  has_next: boolean;
  next_page: number | null;
}

// --- Token usage: attributed (in-app) vs account-wide (CloudWatch) ---------

export interface TokenBucket {
  input_tokens: number;
  output_tokens: number;
}

export interface TokenUsageCoverage {
  success: boolean;
  month: string;
  attributed: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    by_feature: Record<string, TokenBucket>;
    by_model: Record<string, TokenBucket>;
    /** model id -> feature -> tokens. Observed fact: which features drove each model. */
    by_model_feature: Record<string, Record<string, number>>;
  };
  account: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    by_model: Record<string, TokenBucket>;
    is_final: boolean;
    /** False until the CloudWatch sync job has written a row for this month. */
    available: boolean;
  };
  /** Account tokens no request of ours claimed. Null when account data is absent. */
  unattributed_tokens: number | null;
  /** Null when account data is absent - never shown as 0, which would read as "nothing tracked". */
  coverage_percent: number | null;
  /** model id -> what the app is configured to use it for. Fallback when nothing observed. */
  model_roles: Record<string, string>;
}

export interface BedrockDailyCost {
  date: string; // YYYY-MM-DD
  total_cost: number;
  cost_data: Record<string, number>; // usage_type -> cost
  currency: string;
  is_final: boolean;
  fetched_at?: string;
}

export interface BedrockDailyCostResponse {
  daily: BedrockDailyCost[];
  pagination: PaginationData;
  currency: string;
  total_cost: number;
}

export interface BedrockMonthlyCost {
  month: string; // YYYY-MM
  total_cost: number;
  cost_data: Record<string, number>;
  currency: string;
  is_final: boolean;
}

export interface BedrockCostSummary {
  total_cost: number;
  cost_data: Record<string, number>;
  currency: string;
  is_final: boolean;
}

export interface BedrockMonthlyCostResponse {
  monthly: BedrockMonthlyCost[];
  summary: BedrockCostSummary;
}

export interface GetUsersResponse {
  users: User[];
  pagination: PaginationData;
}

export interface CreateKnowledgeBaseRequest {
  knowledgebase_name: string;
  index_name: string;
  description: string;
  department?: string;
  file_ids?: string[];
  summary?: string;
}

export interface UpdateKnowledgeBaseRequest {
  summary?: string;
  file_ids?: string[];
  description?: string;
  department?: string;
}

export enum FileStatus {
  NOT_PROCESSED = "not_processed",
  PROCESSING = "processing",
  PROCESSED = "processed",
  FAILED = "failed",
}

export interface ScanIssue {
  type: string;
  label: string;
  value: string;
  redacted_value: string;
  action: string;
  source: string;
  severity: "HIGH" | "MEDIUM" | "LOW";
}

/**
 * Admin lock, applied by an admin to something they created.
 *
 * While locked the resource is exclusive to `locked_by` -- the API hides it
 * from everybody else's lists and rejects their requests with 423. Both fields
 * are optional: absent means unlocked, which is every resource until an admin
 * locks one.
 */
export interface Lockable {
  locked_by?: string | null;
  locked_at?: string | null;
}

export type LockResourceType = "brainbank" | "va" | "file";
export type LockFileType = "session" | "brainbank";

export interface LockStateResponse {
  resource_type: string;
  resource_id: string;
  locked: boolean;
  locked_by?: string | null;
  locked_at?: string | null;
  /** People whose share or assignment the lock cleared; each was notified. */
  revoked_users?: number;
  /** Managers the lock removed from a VA; each was notified. */
  revoked_managers?: number;
}

export interface FileItem extends Lockable {
  id: string;
  file_name: string;
  uploaded_by?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  knowledge_base_id?: string | null;
  status?: FileStatus;
  scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending";
  scan_issues?: ScanIssue[];
  scan_findings?: { type: string; count: number }[];
  last_scan_date?: string | null;
  scan_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
  uploaded_at?: string | null;
}

export interface Model {
  id: string;
  foundation_model_id: string;
  name: string;
  display_name: string;
  provider: string;
  foundation_model_type: string;
  max_tokens?: string;
  long_description?: string;
  short_description?: string;
  attributes?: string[];
  inference_profile_id?: string;
  is_enabled: boolean;
  file_upload_support: boolean;
  tool_call_support: boolean;
  /** False = not recommended for a VA with connectors enabled. The model stays
   *  enabled and selectable by hand; only the AI recommendation skips it. */
  connector_support?: boolean;
  default: boolean;
  /** The model voice mode switches to. Only ever true on a voice (Nova Sonic) model. */
  default_voice?: boolean;
  /** Relative running cost: 1 = economy, 2 = standard, 3 = premium.
   *  Always sent by the API (derived when an admin hasn't set one). */
  cost_tier?: number;
  /** Effort levels (thinking depth) the model takes, low .. max. Empty = no Effort menu. */
  supported_efforts?: string[];
  /** The model's own effort level when the user hasn't picked one. */
  default_effort?: string | null;
}

/** Result of automatic model selection for a Virtual Assistant. */
export interface VAModelSuggestion {
  model_id: string;
  display_name: string;
  cost_tier: number;
  /** "unknown" when selection fell back without analysing the VA. */
  complexity: "simple" | "moderate" | "complex" | "unknown";
  reason: string;
  fallback_used: boolean;
}

export interface Hat extends Lockable {
  id?: string;
  user_id: string;
  hat_name: string;
  summary: string;
  initial_prompt: string;
  directions: string;
  is_public: boolean;
  model_id?: string;
  feature_keys: {
    image_creation: boolean;
    internet_browsing: boolean;
    kyva_llm: boolean;
    kyva_connectors: boolean;
    include_knowledge_base: boolean;
  };
  created_at: string;
  updated_at: string;
  last_updated?: string;
  favorites?: { [key: string]: boolean | { BOOL: boolean } };
}

// Workflow types — sequential chain of 2 to 5 assistants
export interface Workflow {
  id?: string;
  user_id?: string;
  workflow_name: string;
  description: string;
  assistant_chain: string[]; // 2 to 5 assistant IDs, ordered A1 -> A2 -> ... -> AN
  model_id?: string; // Optional workflow-level model; overrides VA / chat-session models
  is_public: boolean;
  created_at?: string;
  updated_at?: string;
  favorites?: { [key: string]: boolean | { BOOL: boolean } };
}

export interface WorkflowListResponse {
  workflows: Workflow[];
  pagination: PaginationData;
}

interface LicenseValidity {
  Begin?: string;
  end?: string;
}

export interface LicenseInfo {
  logical_license_id: string | null;
  validity: LicenseValidity;
}

export interface MemoryToggleRequest {
  enabled: boolean;
}

export interface UserFileManagementResponse {
  session_files: Array<{
    file_id: string;
    file_name: string;
    uploaded_by_id: string;
    uploaded_by_name: string;
    session_id: string;
    session_title: string;
    storage_location: 'session' | 'brainbank' | 'direct';
    uploaded_at: string;
    status: string;
    scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
    scan_issues?: ScanIssue[];
    scan_findings?: { type: string; count: number }[];
    last_scan_date?: string | null;
    scan_count?: number;
    locked_by?: string | null;
    locked_at?: string | null;
  }>;
  brainbank_files: Array<{
    department: string | null | undefined;
    file_id: string;
    file_name: string;
    uploaded_by_id: string;
    uploaded_by_name: string;
    knowledge_base_id: string;
    knowledge_base_name: string;
    storage_location: 'session' | 'brainbank' | 'direct';
    uploaded_at: string;
    status: string;
    is_creator?: boolean;
    processing_mode?: string;
    scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
    scan_issues?: ScanIssue[];
    scan_findings?: { type: string; count: number }[];
    last_scan_date?: string | null;
    scan_count?: number;
    locked_by?: string | null;
    locked_at?: string | null;
  }>;
  pagination: PaginationInfo;
  search?: {
    keyword: string;
    total_matches: number;
    searched_fields: string[];
  };
  stats: {
    total_files: number;
    session_files_count: number;
    brainbank_files_count: number;
    is_admin_view: boolean;
    current_page_items: number;
    is_search_result?: boolean;
    safe_files_count?: number;
    flagged_files_count?: number;
    failed_scans_count?: number;
    not_scanned_count?: number;
    total_credit_cards?: number;
    total_emails?: number;
    total_phones?: number;
  };
}

export interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  next_page: number | null;
  previous_page: number | null;
}

export interface AdminFileManagementResponse {
  session_files: Array<{
    file_id: string;
    file_name: string;
    uploaded_by_id: string;
    uploaded_by_name: string;
    session_id: string;
    session_title: string;
    storage_location: 'session' | 'brainbank' | 'direct';
    uploaded_at: string;
    status?: string;
    scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
    scan_issues?: ScanIssue[];
    scan_findings?: { type: string; count: number }[];
    last_scan_date?: string | null;
    scan_count?: number;
    locked_by?: string | null;
    locked_at?: string | null;
  }>;
  brainbank_files: Array<{
    department: string | null | undefined;
    file_id: string;
    file_name: string;
    uploaded_by_id: string;
    uploaded_by_name: string;
    knowledge_base_id: string;
    knowledge_base_name: string;
    storage_location: 'session' | 'brainbank' | 'direct';
    uploaded_at: string;
    status: string;
    processing_mode?: string;
    scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
    scan_issues?: ScanIssue[];
    scan_findings?: { type: string; count: number }[];
    last_scan_date?: string | null;
    scan_count?: number;
    locked_by?: string | null;
    locked_at?: string | null;
  }>;
  pagination: PaginationInfo;
  search?: {
    keyword: string;
    total_matches: number;
    searched_fields: string[];
  };
  stats: {
    total_files: number;
    session_files_count: number;
    brainbank_files_count: number;
    total_uploaders: number;
    current_page_items: number;
    is_search_result?: boolean;
    safe_files_count?: number;
    flagged_files_count?: number;
    failed_scans_count?: number;
    not_scanned_count?: number;
    total_credit_cards?: number;
    total_emails?: number;
    total_phones?: number;
  };
}

export interface FileSearchRequest {
  search: string;
  page?: number;
  page_size?: number;
}

export interface SearchMetadata {
  keyword: string;
  total_matches: number;
  searched_fields: string[];
}

export interface FileSearchResponse {
  session_files: any[];
  brainbank_files: any[];
  pagination: PaginationInfo;
  search: SearchMetadata;
  stats: {
    total_files: number;
    session_files_count: number;
    brainbank_files_count: number;
    current_page_items: number;
    is_search_result: boolean;
    is_admin_view?: boolean;
    total_uploaders?: number;
  };
}

export interface FeedbackRequest {
  name: string;
  from_email: string;
  to_email: string;
  subject: string;
  message: string;
}

export interface FeedbackResponse {
  success: boolean;
  message: string;
  data?: {
    user_id: string;
    from_email: string;
    to_email: string;
    message_id: string;
    attachments_count?: number;
    total_size_kb?: number;
  };
}

export interface GuardrailSettings {
  enabled: boolean;
  restricted_words: string[];
}

export interface GuardrailSettingsResponse {
  success: boolean;
  message?: string;
  data: GuardrailSettings;
}

export interface PIIEntity {
  type: string;
  inputAction: 'BLOCK' | 'ANONYMIZE';
  outputAction: 'BLOCK' | 'ANONYMIZE';
  inputEnabled: boolean;
  outputEnabled: boolean;
}

export interface RegexPattern {
  name: string;
  pattern: string;
  inputAction: 'BLOCK' | 'ANONYMIZE';
  outputAction: 'BLOCK' | 'ANONYMIZE';
  inputEnabled: boolean;
  outputEnabled: boolean;
  description?: string;
}

export interface SensitiveInfoSettings {
  piiEntities: PIIEntity[];
  regexPatterns: RegexPattern[];
}

export interface GuardrailSettings {
  enabled: boolean;
  file_scan_enabled?: boolean;
  restricted_words: string[];
  sensitiveInfo?: SensitiveInfoSettings;
}

export interface GuardrailSettingsResponse {
  success: boolean;
  message?: string;
  data: GuardrailSettings;
}

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  status: 'unread' | 'read';
  va_id: string;
  va_name: string;
  shared_by_user_id: string;
  action_url: string;
  created_at: string;
  read_at: string | null;
}

export interface NotificationResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    items_per_page: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    next_page: number | null;
  };
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface NotificationActionResponse {
  message: string;
  updated_count?: number; // For mark all as read
}

// ============ Meeting Recording Types ============

export type RecordingStatus =
  | 'recording'
  | 'uploading'
  | 'processing'
  | 'transcribing'
  | 'transcribed'
  | 'completed'
  | 'failed';

export type ParticipantRole = string; // Freeform text for any role

export interface SpeakerMapping {
  speaker_label: string;
  participant_name: string;
  participant_role: ParticipantRole;
}

export interface TranscriptionSegment {
  speaker_label: string;
  start_time: number;
  end_time: number;
  content: string;
  confidence: number;
}

export interface Transcription {
  content: TranscriptionSegment[];
  full_text: string;
  language_code: string;
}

export interface MeetingSession {
  id: string;
  user_id: string;
  title: string;
  description: string;
  recording_status: RecordingStatus;
  audio_s3_key?: string;
  audio_duration_seconds?: number;
  total_chunks?: number;
  uploaded_chunks: number[];
  transcription_job_id?: string;
  // Why the batch job could not produce a transcript — AWS Transcribe's
  // own FailureReason. Describes one job, so a retry clears it.
  transcription_failure_reason?: string | null;
  speaker_mapping: SpeakerMapping[];
  created_at: string;
  updated_at: string;
  status_changed_at?: string;
  // WS-01 live transcript streaming
  audio_deleted_at?: string;
  live_transcript_complete?: boolean;
  live_gap_count?: number;
  // WS-02 — BrainBanks this transcript has been sent to
  brain_bank_exports?: BrainBankExport[];
  // WS-03 — transcript imported from a PDF (metadata; text is fetched separately)
  imported_transcript?: ImportedTranscriptMeta | null;
}

// WS-03 — what the UI knows about an imported transcript without its text.
export interface ImportedTranscriptMeta {
  file_name: string;
  page_count?: number;
  char_count?: number;
  imported_at: string;
  imported_by?: string;
}

// The imported transcript with its full text, exactly as extracted — shown
// as it appears in the PDF, never reformatted or speaker-parsed.
export interface ImportedTranscript extends ImportedTranscriptMeta {
  text: string;
}

// What the automatic BrainBank re-sync did after an import or delete.
export interface BrainBankSyncSummary {
  synced: number;
  removed: number;
  failed: number;
}

// One copy of a session transcript living in a BrainBank (WS-02). Identity
// only — processing state is read from the BrainBank's own file record, so a
// failed ingest can never show as ready.
export interface BrainBankExport {
  knowledge_base_id: string;
  knowledge_base_name: string;
  index_name: string;
  // Which file this record points at. A meeting with an imported PDF sends two
  // files to a BrainBank: the transcript document and the PDF itself. Absent on
  // records written before the PDF was sent separately — read those as
  // 'transcript'.
  kind?: 'transcript' | 'imported_pdf';
  file_id: string;
  file_name: string;
  original_file_name: string;
  content_hash: string;
  client_request_id: string;
  exported_at: string;
  exported_by: string;
}

export type BrainBankExportStatus =
  | 'sent'
  | 'replaced'
  // The content had not changed since the last send: nothing was re-indexed.
  | 'already_sent'
  // The same request id was already served (double-click, timeout retry).
  | 'duplicate_request';

// A chat attachment's copy in a BrainBank. Identity only -- processing state
// comes from getFile(file_id), as with BrainBankExport.
export interface ChatBrainBankSync {
  knowledge_base_id: string;
  knowledge_base_name: string;
  index_name: string;
  file_id: string;
  file_name: string;
  original_file_name: string;
  client_request_id: string;
  synced_at: string;
  synced_by: string;
}

export type ChatBrainBankSyncStatus = 'sent' | 'already_sent' | 'duplicate_request';

export interface ChatBrainBankSyncResult {
  status: ChatBrainBankSyncStatus;
  sync: ChatBrainBankSync;
  scan_result?: { status?: string; issues?: unknown[] } | null;
}

// The caller's attachments in one conversation that were added to a BrainBank.
export interface SessionFileBrainBankSyncs {
  session_file_id: string;
  // The stored name, the same one the message carries in meta.files.
  file_name: string;
  original_file_name: string;
  syncs: ChatBrainBankSync[];
}

export interface BrainBankExportResult {
  status: BrainBankExportStatus;
  export: BrainBankExport;
  // False when the document reached the BrainBank but the session record did
  // not — the copy exists, and a re-send heals the bookkeeping.
  export_recorded: boolean;
  // Only meaningful for 'replaced'; false means the older copy is still there.
  previous_removed?: boolean | null;
  scan_result?: { status?: string; issues?: unknown[] } | null;
}

// One line of the live transcript panel (WS-01). `speaker` is the raw
// generation-namespaced key from the stream ("g0:0"); display naming
// (Speaker 1/2) is assigned client-side by first appearance.
export interface LiveTranscriptSegment {
  id: string;
  text: string;
  isStable: boolean;
  speaker: string | null;
  absStartMs: number;
  absEndMs: number;
}

export type TranscriptStreamStatus =
  | "idle"         // no session
  | "connecting"   // socket opening
  | "live"         // streaming
  | "reconnecting" // dropped, retrying with backfill
  | "paused"       // recording paused — stream closed on purpose, reopens on resume
  | "interrupted"  // gave up — recording continues (audio-on wording)
  | "completed"    // clean stop
  | "unavailable"; // no endpoint configured / no browser support

export interface CreateMeetingSessionRequest {
  title?: string;
  description?: string;
}

export interface CreateMeetingSessionResponse {
  id: string;
  user_id: string;
  title: string;
  recording_status: RecordingStatus;
  created_at: string;
}

export interface MeetingSessionListResponse {
  sessions: MeetingSession[];
  pagination: PaginationData;
}

export interface ChunkUploadResponse {
  chunk_index: number;
  uploaded: boolean;
  total_uploaded: number;
}

export interface UploadStatusResponse {
  total_chunks: number | null;
  uploaded_chunks: number[];
  missing_chunks: number[];
  status: 'not_started' | 'in_progress' | 'complete' | 'incomplete';
}

export interface FinalizeRequest {
  total_chunks: number;
  duration_seconds: number;
}

export interface FinalizeResponse {
  message: string;
  transcription_job_id?: string;
}

export interface TranscriptionStatusResponse {
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress_percent?: number;
}

export interface UpdateSpeakerMappingRequest {
  mappings: SpeakerMapping[];
}
// ---- Voice Mode -----------------------------------------------------------

/** A document a voice answer was found in, as the backend's BrainBank search reports it. */
export interface VoiceBrainBankSource {
  brainBank: string;
  fileName: string;
  page?: number | null;
}

/** One saved message of a voice conversation, as its transcript shows it. */
export interface VoiceTranscriptEntry {
  id?: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt?: string;
  /** Kyva was cut off: the text is what it said before the user spoke. */
  interrupted?: boolean;
  /** Where the answer came from, when Kyva searched a BrainBank for it. */
  sources?: VoiceBrainBankSource[];
}

/** The BrainBank side of a voice conversation, as the backend reports it. */
export interface VoiceBrainBankState {
  /** BrainBanks this conversation searches (the picked one, or the VA's own). */
  brainBanks: Array<{ id: string; name: string }>;
  /** True once the user chose one: the conversation then keeps it. */
  pinned: boolean;
  /** A BrainBank search is running right now. */
  searching: boolean;
  /** What the running lookup is: the BrainBanks, one document, or opening a BrainBank. */
  searchKind?: 'brainbank' | 'document' | 'spreadsheet' | 'open' | null;
  /** The file name, when one document or spreadsheet is read. */
  searchDocument?: string | null;
}

export const EMPTY_VOICE_BRAINBANK_STATE: VoiceBrainBankState = {
  brainBanks: [],
  pinned: false,
  searching: false,
  searchKind: null,
  searchDocument: null,
};

// BrainBank Dropzone: short-lived upload links handed to an end client.
export type DropzoneExtension = "pdf" | "docx" | "xlsx" | "txt";
export type DropzoneLinkStatus = "active" | "expired" | "revoked";
/** 1 h, 8 h, 24 h, 48 h: the segmented control on the Upload links panel. */
export type DropzoneExpiryMinutes = 60 | 480 | 1440 | 2880;
/** Same choice as the Upload Files dialog: standard queue or the advanced (AI) queue. */
export type DropzoneProcessingMode = "standard" | "advanced";

export interface DropzoneLink {
  link_id: string;
  label: string;
  knowledge_base_id: string;
  created_by: string;
  created_at: string;
  expires_at: string;
  allowed_extensions: DropzoneExtension[];
  processing_mode: DropzoneProcessingMode;
  max_file_bytes: number;
  max_files: number;
  files_issued: number;
  status: DropzoneLinkStatus;
  revoked_at?: string;
  /** "system:passcode-lockout" when the link revoked itself after too many wrong passcodes. */
  revoked_by?: string;
  /** Files wait on the Upload links tab until someone approves them. */
  hold_for_approval: boolean;
  /** The client must also enter a passcode the creator shared separately. */
  passcode_required: boolean;
  passcode_failures?: number;
  /** "Copy link" can fetch the URL again: the link is active and its token is stored on the row. */
  url_available?: boolean;
}

export const PASSCODE_LOCKOUT_ACTOR = "system:passcode-lockout";

/** A link upload waiting for approval, as the Upload links tab lists it. */
export interface HeldDropzoneFile {
  id: string;
  file_name: string;
  file_size?: number | null;
  knowledge_base_id: string;
  dropzone_link_id?: string | null;
  dropzone_label?: string | null;
  uploaded_at?: string | null;
  /** False until the browser (or the server sweep) confirmed the object is in the bucket. */
  arrived: boolean;
  scan_status?: string | null;
}

/** The create response: the only place the link URL (with its token) ever appears. */
export interface CreatedDropzoneLink extends DropzoneLink {
  url: string;
}

export interface CreateDropzoneLinkRequest {
  knowledge_base_id: string;
  label: string;
  expires_in_minutes: DropzoneExpiryMinutes;
  allowed_extensions: DropzoneExtension[];
  processing_mode: DropzoneProcessingMode;
  max_file_mb: 10 | 25 | 50;
  max_files: number;
  /** Optional; 6 to 32 characters. Sent once, hashed on the server, never returned. */
  passcode?: string;
  hold_for_approval: boolean;
}

/** What the public upload page is told about a link. Never the creator's email. */
export interface DropzoneInfo {
  label: string;
  brain_bank_name: string;
  requester_name: string;
  allowed_extensions: DropzoneExtension[];
  max_file_bytes: number;
  max_files: number;
  files_issued: number;
  seconds_left: number;
  passcode_required: boolean;
  hold_for_approval: boolean;
}

/** What a visitor gets before the passcode: that one is needed, nothing else. */
export interface DropzoneGate {
  passcode_required: true;
}

export interface DropzoneUploadGrant {
  file_id: string;
  url: string;
  fields: Record<string, string>;
}
